import { useEffect, useState } from 'react';
import { cn } from '@/lib/classNames';
import { useBorderRotation } from '@/modules/sequencesRedesign/components/AiModal/hooks/useBorderRotation';
import { FourCircles, LogoText2 } from '@/resources/assets/images';

interface Props {
	minimumTimePassed: boolean;
	apiResult: 'success' | 'fail' | null;
}

export default function OnboardingLoadingModal({
	minimumTimePassed,
	apiResult
}: Props) {
	const boxRef = useBorderRotation(true);

	const steps = [
		'Searching for prospects to contact',
		'Writing personalized emails',
		'Preparing final touches'
	];

	const [activeStep, setActiveStep] = useState(0);
	const [completedSteps, setCompletedSteps] = useState<number[]>([]);

	useEffect(() => {
		const timeouts: NodeJS.Timeout[] = [];

		if (!minimumTimePassed) {
			steps.forEach((_, index) => {
				timeouts.push(
					setTimeout(() => {
						setCompletedSteps(prev =>
							[...prev, index - 1].filter(i => i >= 0)
						);
						setActiveStep(index);
					}, index * 5000)
				);
			});
		} else {
			// When minimum time passes, simulate final bullet in progress
			setCompletedSteps([0, 1]);
			setActiveStep(2);
		}

		return () => timeouts.forEach(clearTimeout);
	}, [minimumTimePassed]);

	useEffect(() => {
		if (minimumTimePassed && apiResult) {
			// Delay marking the last bullet green by 2 seconds to show transition
			const timeout = setTimeout(() => {
				setCompletedSteps([0, 1, 2]);
				setActiveStep(-1); // Done
			}, 2000);

			return () => clearTimeout(timeout);
		}
	}, [minimumTimePassed, apiResult]);

	useEffect(() => {
		if (minimumTimePassed && apiResult) {
			setCompletedSteps([0, 1, 2]);
			setActiveStep(-1); // no step actively spinning anymore
		}
	}, [minimumTimePassed, apiResult]);

	const getColor = (index: number) => {
		if (completedSteps.includes(index)) return 'bg-dm-green';
		if (index === activeStep) return 'bg-blue-400';
		return 'bg-yellow-400';
	};

	return (
		<div className="h-full border-l border-dm-gray">
			<div className="relative flex w-full justify-center border-dm-gray px-4 py-6 text-white sm:pt-20">
				<div
					ref={boxRef}
					data-is-loading="true"
					style={
						{
							'--angle': '0deg',
							'--border-color':
								'linear-gradient(var(--angle), #BC82F3, #F5B9EA, #8D99FF, #AA6EEE, #FF6778, #FFBA71, #C686FF)',
							'--bg-color': 'linear-gradient(#212121, #212121)'
						} as React.CSSProperties
					}
					className={cn(
						'w-full max-w-[43.75rem] rounded-2xl border-2 border-transparent p-[2px]',
						"data-[is-loading='true']:border-[#0000]",
						"data-[is-loading='true']:[background:padding-box_var(--bg-color),border-box_var(--border-color)]"
					)}
				>
					<div className="flex h-full flex-col gap-8 overflow-hidden rounded-2xl bg-dm-black3 px-6 pb-20 pt-8 sm:h-[24.5rem] sm:gap-6 sm:pb-14 sm:pl-[3.25rem] sm:pr-6 sm:pt-8">
						<div className="flex flex-col gap-14">
							<div className="flex flex-col gap-4">
								<p className="text-lg font-semibold">
									Generating your customer list and email
									sequence...
								</p>
								<div className="mt-4 flex flex-col gap-3">
									{steps.map((step, index) => (
										<div
											key={index}
											className="flex items-start gap-2"
										>
											<div
												className={cn(
													'h-5 w-5 rounded-full',
													getColor(index)
												)}
											/>

											<span className="font-barlow text-base leading-6">
												{step}
											</span>
										</div>
									))}
								</div>
							</div>
							<div>
								<div className="flex flex-row items-center justify-center">
									<div>
										<LogoText2 className="h-[3rem] w-[13.6875rem]" />
									</div>

									<div>
										<FourCircles className="ai-loading-inner-right-loading-content-icon h-full w-full" />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
